import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIteroOrder, useIteroPatientLookup } from '../../../../hooks/useIteroBrowse';
import { useToast } from '../../../../providers/ToastProvider';
import type { IteroOrder, IteroPatient } from '../../../../services/iteroBrowse';
import { browseErrorText } from './browseErrorText';
import { useIteroBrowserScope } from './iteroBrowserScope';
import type { IteroPatientRef } from './iteroBrowserRoute';
import { isUnavailableError } from './iteroBrowseErrors';

export const PATIENT_UNAVAILABLE_TEXT = 'That patient is no longer available';
export const ORDER_UNAVAILABLE_TEXT = 'That scan is no longer available';

export type Restored<T> =
    | { kind: 'none' }
    | { kind: 'loading' }
    | { kind: 'ready'; value: T }
    | { kind: 'missing' }
    | { kind: 'failed'; message: string; retry: () => void };

const NONE = { kind: 'none' } as const;
const LOADING = { kind: 'loading' } as const;
const MISSING = { kind: 'missing' } as const;

interface QueryOutcome<T> {
    value: T | null;
    isError: boolean;
    error: unknown;
    isSettled: boolean;
}

function restoredFrom<T>(
    outcome: QueryOutcome<T>,
    failedText: string,
    retry: () => void
): Restored<T> {
    if (outcome.value !== null) return { kind: 'ready', value: outcome.value };
    if (outcome.isError) {
        return isUnavailableError(outcome.error)
            ? MISSING
            : { kind: 'failed', message: browseErrorText(outcome.error, failedText), retry };
    }
    return outcome.isSettled ? MISSING : LOADING;
}

// local is the clicked or listed record; only without it does the restore search by chart number.
export function useRestoredPatient(
    ref: IteroPatientRef | null,
    local: IteroPatient | null
): Restored<IteroPatient> {
    const scope = useIteroBrowserScope();
    const needsLookup = ref !== null && local === null;
    const query = useIteroPatientLookup(scope, needsLookup ? ref.chartNumber : null);
    const hasChart = ref !== null && ref.chartNumber.trim() !== '';
    const { data, isError, error, refetch } = query;
    const retry = useCallback(() => void refetch(), [refetch]);
    const refId = ref?.id ?? null;

    return useMemo(() => {
        if (refId === null) return NONE;
        if (local) return { kind: 'ready', value: local };
        if (!hasChart) return MISSING;
        const value = data?.patients.find((patient) => patient.id === refId) ?? null;
        const outcome = { value, isError, error, isSettled: data !== undefined };
        return restoredFrom(outcome, "Couldn't load that patient", retry);
    }, [refId, local, hasChart, data, isError, error, retry]);
}

// The single-order fetch is shared with OrderView (same query key), so restoring adds no request.
export function useRestoredOrder(
    rxId: string | null,
    local: IteroOrder | null
): Restored<IteroOrder> {
    const scope = useIteroBrowserScope();
    const { data, isError, error, refetch } = useIteroOrder(scope, rxId, local ?? undefined);
    const retry = useCallback(() => void refetch(), [refetch]);

    return useMemo(() => {
        if (rxId === null) return NONE;
        const outcome = { value: data ?? local, isError, error, isSettled: false };
        return restoredFrom(outcome, "Couldn't load that scan", retry);
    }, [rxId, data, local, isError, error, retry]);
}

// Drops an unrestorable selection once per id, with a toast, so a re-render can't repeat it.
export function useDropUnavailable(
    restored: Restored<unknown>,
    id: string | null,
    message: string,
    drop: () => void
): void {
    const { showToast } = useToast();
    const droppedId = useRef<string | null>(null);
    const isMissing = restored.kind === 'missing';

    useEffect(() => {
        if (!isMissing || id === null) {
            droppedId.current = null;
            return;
        }
        if (droppedId.current === id) return;
        droppedId.current = id;
        showToast(message, 'error');
        drop();
    }, [isMissing, id, message, drop, showToast]);
}
